'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useState, useMemo, useCallback, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import * as CustomPropTypes from '../../../../constants/PropTypes';
import ImmutablePropTypes from 'react-immutable-proptypes';
import Immutable from 'immutable';
import { WEB_FONT_DEMI_BOLD_WEIGHT, WEB_FONT_REGULAR_WEIGHT } from 'HubStyleTokens/misc';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import Small from 'UIComponents/elements/Small';
import UIIcon from 'UIComponents/icon/UIIcon';
import NestedNavItem from 'ecosystem-marketplace-core/components/nav/NestedNavItem';
import NestedNav from 'ecosystem-marketplace-core/components/nav/NestedNav';
import { BY_CATEGORY as FEATURES_BY_CATEGORY } from 'ecosystem-marketplace-core/constants/groupTags/categoryFeatures';
import * as GROUPED_CATEGORIES from 'ecosystem-marketplace-core/constants/groupTags/SuperCategoryGroup';
import { getSuperCategoryI18nKey, getCategoryI18nKey, getCategoryFeatureI18nKey } from 'ecosystem-marketplace-core/utils/i18n';
import baseSuperCategoryOrder from 'ecosystem-marketplace-core/constants/groupTags/superCategoryOrder';
import * as FilterKey from '../../../../constants/search/FilterKey';
import { filterFeaturesByCategory } from '../../../../utils/groupTags';
import { push, getFullHref } from '../../../../utils/history';
import { usePrevious } from 'ecosystem-marketplace-core/hooks/usePrevious';
import { getAllCategoriesUrl, getUngatedAllCategoriesUrl, getSuperCategoryFilterUrl, getCategoryFilterUrl } from '../../../../utils/urls';
import NestedNavCheckboxItem from '../inputs/NestedNavCheckboxItem';
import UnstyledNestedNavItem from '../inputs/UnstyledNestedNavItem';
import { getIsUngatedForFeaturedApps } from '../../../../selectors/auth';
const EMPTY_SET = Immutable.Set();
const ALL = 'all';
const superCategoryOrder = [ALL, ...baseSuperCategoryOrder];

const getOpenIcon = open => open ? 'up' : 'down';

const FeaturesToggleGroup = ({
  category,
  onChange,
  selectedFeatures
}) => {
  const featuresByCategory = Object.values(FEATURES_BY_CATEGORY[category]);
  const [expanded, setExpanded] = useState(featuresByCategory.length - 5 <= 0);
  const renderedFeatures = useMemo(() => Immutable.Set(featuresByCategory).map(feature => [feature, getCategoryFeatureI18nKey(category, feature)]).sortBy(([, featureI18nKey]) => unescapedText(featureI18nKey)).map(([feature, featureI18nKey]) => /*#__PURE__*/_jsx(NestedNavCheckboxItem, {
    checkboxProps: {
      checked: selectedFeatures.includes(feature),
      onChange,
      value: feature
    },
    title: /*#__PURE__*/_jsx(Small, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: featureI18nKey
      })
    })
  }, feature)).slice(0, expanded ? undefined : 5), [category, expanded, featuresByCategory, onChange, selectedFeatures]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [renderedFeatures, !expanded && /*#__PURE__*/_jsx(UnstyledNestedNavItem, {
      "aria-label": unescapedText('appMarketplace.storefront.filters.categoryFeatures.toggleGroup.ariaLabel', {
        category: unescapedText(getCategoryI18nKey(category))
      }),
      onClick: () => {
        setExpanded(true);
      },
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "appMarketplace.storefront.filters.categoryFeatures.toggleGroup.expandButtonText"
      }),
      use: "link"
    })]
  });
};

const CategoryNavItem = (_ref) => {
  let {
    category,
    filters,
    isActive,
    isAdminPreview,
    isUngatedForFeaturedApps,
    onCategoryChange,
    onFeatureChange,
    selectedFeatures,
    superCategory,
    trackFilterNavItemClick
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["category", "filters", "isActive", "isAdminPreview", "isUngatedForFeaturedApps", "onCategoryChange", "onFeatureChange", "selectedFeatures", "superCategory", "trackFilterNavItemClick"]);

  const prevIsActive = usePrevious(isActive);
  const [open, setOpen] = useState(isActive);
  const [duration, setDuration] = useState();
  const categoryFilters = useMemo(() => {
    return filters.withMutations(mutableFilters => {
      mutableFilters.delete(FilterKey.SEARCH).set(FilterKey.SUPER_CATEGORY, superCategory).set(FilterKey.CATEGORY, category).set(FilterKey.CATEGORY_FEATURES, isActive ? filterFeaturesByCategory(selectedFeatures, category) : EMPTY_SET);
    });
  }, [category, filters, isActive, selectedFeatures, superCategory]);
  const handleFeatureChange = useCallback(evt => {
    if (isAdminPreview) {
      return;
    }

    if (isActive) {
      onFeatureChange(evt);
    } else {
      const {
        target: {
          value
        }
      } = evt;
      push(categoryFilters.set(FilterKey.CATEGORY_FEATURES, EMPTY_SET.add(value)).toLocation(isUngatedForFeaturedApps));
    }
  }, [isAdminPreview, isUngatedForFeaturedApps, categoryFilters, isActive, onFeatureChange]);
  useLayoutEffect(() => {
    if (isActive !== prevIsActive) {
      setOpen(isActive);
      setDuration(0);
    }
  }, [isActive, prevIsActive]);
  return /*#__PURE__*/_jsx(NestedNavItem, Object.assign({}, rest, {
    active: isActive,
    duration: duration,
    href: isAdminPreview ? undefined : getFullHref(getCategoryFilterUrl(category, superCategory)),
    iconRight: ({
      className: iconClassName
    }) => {
      return /*#__PURE__*/_jsx(UIIcon, {
        className: iconClassName,
        name: getOpenIcon(open),
        onClick: evt => {
          evt.stopPropagation();
          evt.preventDefault();
          setOpen(!open);
        },
        size: "xxs"
      });
    },
    onClick: evt => {
      evt.preventDefault();

      if (isAdminPreview) {
        return;
      }

      onCategoryChange(superCategory, category);
      trackFilterNavItemClick(FilterKey.CATEGORY, category);
    },
    onOpenChange: ({
      target: {
        value
      }
    }) => {
      setOpen(value);
      setDuration(undefined);
    },
    open: open,
    style: {
      fontSize: 14
    },
    title: unescapedText(getCategoryI18nKey(category)),
    _rootRenderer: children => /*#__PURE__*/_jsx(NestedNav, {
      className: "m-y-0 p-left-4",
      children: children
    }),
    children: /*#__PURE__*/_jsx(FeaturesToggleGroup, {
      category: category,
      onChange: handleFeatureChange,
      selectedFeatures: selectedFeatures
    })
  }));
};

const SuperCategoryNavItem = (_ref2) => {
  let {
    activeCategory,
    activeSuperCategory,
    filters,
    isAdminPreview,
    isAllCategories,
    isUngatedForFeaturedApps,
    onCategoryChange,
    onFeatureChange,
    selectedFeatures,
    superCategory,
    trackFilterNavItemClick
  } = _ref2,
      rest = _objectWithoutPropertiesLoose(_ref2, ["activeCategory", "activeSuperCategory", "filters", "isAdminPreview", "isAllCategories", "isUngatedForFeaturedApps", "onCategoryChange", "onFeatureChange", "selectedFeatures", "superCategory", "trackFilterNavItemClick"]);

  const isActive = superCategory === ALL ? isAllCategories : superCategory === activeSuperCategory;
  const prevIsActive = usePrevious(isActive);
  const [open, setOpen] = useState(isActive);
  const [duration, setDuration] = useState();
  const superCategoryI18nKey = superCategory === ALL ? 'appMarketplace.storefront.filters.category.items.all.titleText' : getSuperCategoryI18nKey(superCategory);
  const children = useMemo(() => {
    if (superCategory === ALL) {
      return null;
    }

    return Immutable.List(GROUPED_CATEGORIES[superCategory]).map(category => [category, getCategoryI18nKey(category)]).sortBy(([, categoryI18nKey]) => unescapedText(categoryI18nKey)).map(([category]) => /*#__PURE__*/_jsx(CategoryNavItem, {
      "data-selenium-id": `${category}-category`,
      filters: filters,
      isActive: isActive && category === activeCategory,
      isAdminPreview: isAdminPreview,
      isUngatedForFeaturedApps: isUngatedForFeaturedApps,
      category: category,
      onCategoryChange: onCategoryChange,
      onFeatureChange: onFeatureChange,
      selectedFeatures: selectedFeatures,
      superCategory: superCategory,
      trackFilterNavItemClick: trackFilterNavItemClick
    }, category));
  }, [activeCategory, filters, isActive, isAdminPreview, isUngatedForFeaturedApps, onCategoryChange, onFeatureChange, selectedFeatures, superCategory, trackFilterNavItemClick]);
  useLayoutEffect(() => {
    if (isActive !== prevIsActive) {
      setOpen(isActive);
      setDuration(0);
    }
  }, [isActive, prevIsActive]);
  return /*#__PURE__*/_jsx(NestedNavItem, Object.assign({}, rest, {
    active: isActive && !activeCategory,
    duration: duration,
    href: isAdminPreview ? undefined : getFullHref(superCategory === ALL ? isUngatedForFeaturedApps ? getUngatedAllCategoriesUrl() : getAllCategoriesUrl() : getSuperCategoryFilterUrl(superCategory)),
    iconRight: ({
      className: iconClassName
    }) => {
      if (children == null) {
        return null;
      }

      return /*#__PURE__*/_jsx(UIIcon, {
        className: iconClassName,
        name: getOpenIcon(open),
        onClick: evt => {
          evt.stopPropagation();
          evt.preventDefault();
          setOpen(!open);
        },
        size: "xxs"
      });
    },
    onClick: evt => {
      evt.preventDefault();

      if (isAdminPreview) {
        return;
      }

      onCategoryChange(superCategory !== ALL ? superCategory : undefined);
      trackFilterNavItemClick(FilterKey.SUPER_CATEGORY, superCategory === ALL ? 'all categories' : superCategory);
    },
    onOpenChange: ({
      target: {
        value
      }
    }) => {
      setOpen(value);
      setDuration(undefined);
    },
    open: open,
    style: {
      fontWeight: isActive ? WEB_FONT_DEMI_BOLD_WEIGHT : WEB_FONT_REGULAR_WEIGHT
    },
    title: unescapedText(superCategoryI18nKey),
    children: children
  }));
};

const CategoryFilterNav = ({
  activeCategory,
  activeSuperCategory,
  filters,
  isAdminPreview,
  isAllCategories,
  onCategoryChange,
  onFeaturesChange,
  selectedFeatures,
  trackFilterNavItemClick
}) => {
  const isUngatedForFeaturedApps = useSelector(getIsUngatedForFeaturedApps);
  const handleFeatureChange = useCallback(({
    target: {
      checked,
      value
    }
  }) => {
    onFeaturesChange(checked ? selectedFeatures.union([value]) : selectedFeatures.subtract([value]));
  }, [onFeaturesChange, selectedFeatures]);
  return /*#__PURE__*/_jsx(NestedNav, {
    className: "m-bottom-0",
    children: superCategoryOrder.map(superCategory => /*#__PURE__*/_jsx(SuperCategoryNavItem, {
      "data-selenium-id": `${superCategory}-super-category`,
      activeCategory: activeCategory,
      activeSuperCategory: activeSuperCategory,
      filters: filters,
      isAdminPreview: isAdminPreview,
      isAllCategories: isAllCategories,
      isUngatedForFeaturedApps: isUngatedForFeaturedApps,
      onCategoryChange: onCategoryChange,
      onFeatureChange: handleFeatureChange,
      selectedFeatures: selectedFeatures,
      superCategory: superCategory,
      trackFilterNavItemClick: trackFilterNavItemClick
    }, superCategory))
  });
};

CategoryFilterNav.propTypes = {
  activeCategory: CustomPropTypes.category,
  activeSuperCategory: CustomPropTypes.superCategory,
  filters: CustomPropTypes.storefrontFilters.isRequired,
  isAdminPreview: PropTypes.bool,
  isAllCategories: PropTypes.bool.isRequired,
  onCategoryChange: PropTypes.func.isRequired,
  onFeaturesChange: PropTypes.func.isRequired,
  selectedFeatures: ImmutablePropTypes.iterableOf(CustomPropTypes.categoryFeature).isRequired,
  trackFilterNavItemClick: PropTypes.func.isRequired
};
export default CategoryFilterNav;